import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { cancelarOs } from '../api/ordens';
import { mensagemDeErro, MSG_ERRO_REDE } from '../lib/erros';
import { ApiError } from '../lib/http';
import { Drawer } from './Drawer';
import { useToast } from './toast';
import { AreaTexto, BlocoAviso, Botao } from './ui';

/** Cancelar OS — só relatório, não reverte voucher (UX-PAINEL-INTERNO §5). */
export function DrawerCancelarOS({
  osId,
  numeroOs,
  aberto,
  aoFechar,
}: {
  osId: string;
  numeroOs: string;
  aberto: boolean;
  aoFechar: () => void;
}): ReactNode {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();

  const confirmar = async (): Promise<void> => {
    if (!motivo.trim()) {
      setErro('Informe o motivo do cancelamento.');
      return;
    }
    setEnviando(true);
    setErro(null);
    try {
      await cancelarOs(osId, motivo.trim());
      toast(`OS ${numeroOs} marcada como cancelada.`);
      await qc.invalidateQueries({ queryKey: ['ordens'] });
      await qc.invalidateQueries({ queryKey: ['cliente'] });
      setMotivo('');
      aoFechar();
    } catch (err) {
      setErro(err instanceof ApiError && !err.ehRede ? mensagemDeErro(err.codigo) : MSG_ERRO_REDE);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Drawer aberto={aberto} titulo={`Cancelar OS ${numeroOs}`} aoFechar={aoFechar}>
      {erro && <BlocoAviso tom="erro">{erro}</BlocoAviso>}
      <BlocoAviso tom="aviso">
        Cancelar a OS serve só para o relatório. Os vouchers já concedidos por ela continuam valendo
        e não são revertidos. Para corrigir um brinde dado por engano, registre uma anotação no
        histórico — não cancele esperando desfazer.
      </BlocoAviso>
      <AreaTexto
        rotulo="Motivo do cancelamento"
        obrigatorio
        placeholder="Ex.: pagamento estornado; OS lançada em duplicidade."
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
      />
      <div className="flex gap-2">
        <Botao variante="secundario" onClick={aoFechar}>
          Voltar
        </Botao>
        <Botao variante="perigo" carregando={enviando} onClick={() => void confirmar()}>
          Cancelar OS {numeroOs}
        </Botao>
      </div>
    </Drawer>
  );
}
